import React, { useState } from "react";
import { SocialNetworksId } from "@/interfaces";
import { Card } from "@/components/ui";
import {
  SocialHelpGuide,
  SocialAlert,
  SocialProfileUnlinkedState,
  SocialProfileSyncingState,
  SocialProfileConnectedState,
  SocialProfileLoadingState,
  SocialProfileDisconnectingState,
  AccountSelectionDialog,
  ZillowConnectDialog,
  ZillowReviewsImportDialog,
  SocialProfileCardHeader,
  isTwoStepNetwork,
  isReviewNetwork,
  SocialProfileReviewPrefsDialog,
} from "@/components/SocialNetworkProfiles/components";
import { CardAlert, useSocialConnection, useSocialProfiles } from "@/utils";
import { TiktokPreferences } from "@/components/TiktokPreferences";
import { AccountOption, SocialNetworkProfileServices } from "@/services/bedrock";
import { toast } from "sonner";

interface Props {
  userId: string | number;
  networkId: SocialNetworksId;
}

export function SocialProfileCard({ userId, networkId }: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showConfigurationModal, setShowConfigurationModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [showSelectionDialog, setShowSelectionDialog] = useState(false);
  const [cardAlert, setCardAlert] = useState<CardAlert>();
  const [needsHelp, setNeedsHelp] = useState(false);
  const [showZillowConnectDialog, setShowZillowConnectDialog] = useState(false);
  const [showZillowReviewsImportDialog, setShowZillowReviewsImportDialog] = useState(false);
  const [isPrefsDialogOpen, setIsPrefsDialogOpen] = useState(false);

  const { clearSocialProfilesCache } = useSocialProfiles(userId);
  const {
    state,
    socialProfile,
    socialIdentity,

    isLoading,
    isFetching,
    connect,
    pullOptions,
    activateProfile,
    cancelActivation,
    disconnect,
  } = useSocialConnection({
    userId,
    networkId,
    handleOptions: options => {
      setAccountOptions(options);
      setShowSelectionDialog(true);
    },
    handleCardAlert: alert => {
      setCardAlert(alert);
      setNeedsHelp(!!alert);
    },
    handleConfiguration: () => setShowConfigurationModal(true),
  });

  const showReviewPrefs =
    isReviewNetwork(networkId) && state === "CONNECTED" && !!socialProfile && socialProfile.isActive;

  const handleConnect = () => {
    if (networkId === "ZL") {
      setCardAlert(undefined);
      setShowZillowConnectDialog(true);
      return;
    }
    connect();
  };

  const handleSync = async () => {
    if (!socialProfile?.id) return;
    try {
      await SocialNetworkProfileServices.syncProfile(userId, socialProfile.id);
      toast.success("Sync in progress!", {
        description: "Your profile will be updated with the latest data in a few minutes.",
        descriptionClassName: "text-slate-700 dark:text-slate-300",
      });
    } catch (error) {
      console.error("Failed to sync profile:", error);
      toast.error("Failed to request sync. Please try again later.");
    }
  };

  const renderCardContent = () => {
    if (showHelp) return <SocialHelpGuide networkId={networkId} onBack={() => setShowHelp(false)} />;
    if (isLoading || state === undefined) return <SocialProfileLoadingState />;

    if (socialIdentity && state !== "NOT_LINKED")
      return (
        <SocialProfileConnectedState
          socialIdentity={socialIdentity}
          userId={userId}
          onEditReviewPrefs={showReviewPrefs ? () => setIsPrefsDialogOpen(true) : undefined}
        />
      );
    if (state === "SYNCING") return <SocialProfileSyncingState />;

    return (
      <SocialProfileUnlinkedState
        networkId={networkId}
        isConnecting={state === "CONNECTING"}
        connect={handleConnect}
        needsHelp={needsHelp}
        setShowHelp={setShowHelp}
      />
    );
  };

  return (
    <>
      <Card
        variant={state === "NOT_LINKED" ? "dashed" : "default"}
        className={`
          group relative flex flex-col h-full overflow-hidden
          transition-all duration-300 ease-out
          ${
            state === undefined
              ? "border-0"
              : state === "NOT_LINKED"
              ? "hover:bg-slate-50/50"
              : isMenuOpen
              ? "-translate-y-1 shadow-md border-slate-300/0 ring-transparent"
              : "hover:-translate-y-1 hover:shadow-md border-slate-300/0"
          }
        `}
      >
        {/* Header Section */}
        <Card.Header>
          <SocialProfileCardHeader
            profileState={state}
            networkId={networkId}
            isLoading={isLoading}
            isChild={socialProfile?.isChild}
            showMenu={state === "SYNCING" || state === "CONNECTED"}
            menuDisabled={isFetching}
            onOpenChange={setIsMenuOpen}
            handleNeedHelp={() => setShowHelp(true)}
            disconnect={disconnect}
            sync={state === "CONNECTED" && socialProfile?.isActive ? handleSync : undefined}
            switchAccounts={isTwoStepNetwork(networkId) ? () => pullOptions() : undefined}
            importAllZillowReviews={
              networkId === "ZL" &&
              state === "CONNECTED" &&
              !!socialIdentity?.publicUrl?.trim() &&
              socialIdentity.publicUrl.includes("/lender-profile/")
                ? () => setShowZillowReviewsImportDialog(true)
                : undefined
            }
            configure={() => setShowConfigurationModal(true)}
            onEditReviewPrefs={showReviewPrefs ? () => setIsPrefsDialogOpen(true) : undefined}
          />
        </Card.Header>

        <Card.Content className="grow flex flex-col relative gap-2">
          {/* Disconnecting Overlay */}
          {state === "DISCONNECTING" && <SocialProfileDisconnectingState />}

          {cardAlert && (
            <SocialAlert
              alert={cardAlert}
              clearAlert={() => setCardAlert(undefined)}
              onClickAction={id => {
                if (id === "SHOW_HELP") {
                  setShowHelp(true);
                } else if (id === "RECONNECT") {
                  handleConnect();
                }
              }}
            />
          )}
          {renderCardContent()}
        </Card.Content>
      </Card>

      {networkId === "ZL" && (
        <>
          <ZillowConnectDialog
            open={showZillowConnectDialog}
            onOpenChange={setShowZillowConnectDialog}
            userId={userId}
            onConnectComplete={clearSocialProfilesCache}
          />
          <ZillowReviewsImportDialog
            open={showZillowReviewsImportDialog}
            onOpenChange={setShowZillowReviewsImportDialog}
            userId={userId}
            profileUrl={socialIdentity?.publicUrl}
            onComplete={clearSocialProfilesCache}
          />
        </>
      )}

      {showSelectionDialog && (
        <AccountSelectionDialog
          open={showSelectionDialog}
          accountOptions={accountOptions}
          onCancel={() => {
            setShowSelectionDialog(false);
            cancelActivation();
          }}
          onConfirm={option => {
            setShowSelectionDialog(false);
            activateProfile(socialProfile?.id, option).catch(console.error);
          }}
        />
      )}

      {networkId === "TT" && state === "CONNECTED" && showConfigurationModal && (
        <TiktokPreferences
          userId={userId}
          open={showConfigurationModal}
          onClose={() => {
            setShowConfigurationModal(false);
          }}
        />
      )}

      {showReviewPrefs && socialIdentity && (
        <SocialProfileReviewPrefsDialog
          open={isPrefsDialogOpen}
          onOpenChange={setIsPrefsDialogOpen}
          socialIdentity={socialIdentity}
          userId={userId}
        />
      )}
    </>
  );
}
